import { JSONContent } from '@tiptap/core';
import { generateJSON } from '@tiptap/html';
import { Node } from '@tiptap/pm/model';
import { compact, isEqual } from 'lodash';
import { AnyFontSize, FontFamily } from './font';
import { tipTapExtensionsFullList, tipTapLeanExtensions, tipTapSchema } from './tip-tap-schema';

export type ProsemirrorOptions = {
  fontSize?: AnyFontSize;
  fontFamily?: FontFamily;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  underline?: boolean;
  color?: string;
};

export type GenericContent<T> = {
  content: T[];
};

export type StringWithTextStyle = {
  string: string;
  type: 'text' | 'hardbreak';
  options?: ProsemirrorOptions;
};

export type ParagraphContent = GenericContent<StringWithTextStyle> & {
  type: 'text';
};

export type ListContent = {
  type: 'list';
  listType: 'unorderedList' | 'orderedList';
  content: (ListContent | ParagraphContent)[];
};

export type TextBoxContent = GenericContent<ListContent | ParagraphContent>;

export type DocJSONContent = JSONContent;

export function createTipTapTextComponent({ string, options }: Omit<StringWithTextStyle, 'type'>): DocJSONContent {
  return {
    type: 'text',
    text: string,
    marks: compact([
      {
        type: 'textStyle',
        attrs: {
          fontSize: options?.fontSize,
          fontFamily: options?.fontFamily,
          color: options?.color,
        },
      },
      options?.bold && {
        type: 'bold',
        attrs: {},
      },
      options?.italic && {
        type: 'italic',
        attrs: {},
      },
      options?.underline && {
        type: 'underline',
        attrs: {},
      },
      options?.strikethrough && {
        type: 'strike',
        attrs: {},
      },
    ]),
  };
}

export function createTipTapHardbreakComponent(): DocJSONContent {
  return {
    type: 'hardBreak',
  };
}

export function parseParagraph(texts: StringWithTextStyle[]): DocJSONContent {
  return {
    type: 'paragraph',
    content: texts.map(({ string, type, options }) =>
      type === 'hardbreak' ? createTipTapHardbreakComponent() : createTipTapTextComponent({ string, options })
    ),
  };
}

export function parseListItem(text: ParagraphContent | ListContent): DocJSONContent {
  if (text.type === 'list') {
    return parseList(<ListContent>{ content: text.content, listType: text.listType });
  } else {
    return {
      type: 'customListItem',
      content: [parseParagraph(text.content)],
      attrs: { color: text.content[0]?.options?.color },
    };
  }
}

export function parseList({ content, listType }: ListContent): DocJSONContent {
  const result: JSONContent[] = [];

  for (const item of content) {
    if (item.type === 'text') {
      result.push(parseListItem(item));
    } else {
      const obj = parseListItem(item);
      if (obj) {
        result[result.length - 1].content?.push(obj);
      }
    }
  }

  return {
    type: listType === 'orderedList' ? 'customOrderedList' : 'customBulletList',
    content: result,
  };
}

export function stringToProsemirror(string: string, options?: ProsemirrorOptions): DocJSONContent {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: string ? [createTipTapTextComponent({ string, options })] : [],
      },
    ],
  };
}

export function stringToLeanProsemirror(string: string): DocJSONContent {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: string
          ? [
              {
                type: 'text',
                text: string,
              },
            ]
          : [],
      },
    ],
  };
}

export function newMultipleStringsToProsemirror({ content }: TextBoxContent): DocJSONContent {
  if (content.length === 0) return emptyDocument();

  const textBoxContent = content.map(({ content, type, ...rest }) => {
    switch (type) {
      case 'list':
        return parseList({ content, type, listType: (<ListContent>rest).listType });
      default:
      case 'text':
        return parseParagraph(content);
    }
  });

  return {
    type: 'doc',
    content: [...textBoxContent],
  };
}

export function urlToProsemirror(url: string): DocJSONContent {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: url
          ? [
              {
                type: 'text',
                text: url,
                marks: [
                  {
                    type: 'link',
                    attrs: {
                      href: url,
                      target: '_blank',
                      rel: 'noopener noreferrer nofollow',
                    },
                  },
                ],
              },
            ]
          : [],
      },
    ],
  };
}

export function emptyDocument(): DocJSONContent {
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
      },
    ],
  };
}

export function isEmptyDocument(jsonContent: JSONContent): boolean {
  return (
    // JSONContent from empty Y.Fragment
    isEqual(jsonContent, emptyDocument()) ||
    // JSONContent after adding any content and then clearing it
    isEqual(jsonContent, {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })
  );
}

export function htmlToProsemirror(html: string): JSONContent {
  const prosemirrorJson = generateJSON(html, tipTapExtensionsFullList);
  return prosemirrorJson;
}

export function htmlToProsemirrorWithLeanExtentions(html: string): JSONContent {
  const prosemirrorJson = generateJSON(html, tipTapLeanExtensions);
  return prosemirrorJson;
}

export function toJSONContent(maybeJson: string | object): JSONContent | null {
  try {
    return validateJSONContent(maybeJson);
  } catch {
    return null;
  }
}

export function validateJSONContent(maybeJson: string | object): JSONContent {
  const json = typeof maybeJson === 'string' ? JSON.parse(maybeJson) : maybeJson;
  const contentNode = Node.fromJSON(tipTapSchema, json);
  if (contentNode.type.name === 'doc' && contentNode.childCount === 0) {
    return emptyDocument();
  }
  contentNode.check();
  return json as JSONContent;
}
